import React, { useEffect, useMemo, useState, useRef, useContext } from 'react'
import injectSheet from 'react-jss'
import { Table, HeadCell, TableRow, Cell } from 'pobble-components/dist/table'
import {
  BulkActionProvider,
  SelectAllButton,
  BulkCheckbox
} from 'pobble-components/dist/bulk-action'
import { Link, withRouter } from 'react-router-dom'
import ContextMenu from './context-menu/context-menu.container'
import { useMe } from 'pobble-components/dist/pobble-user'
import { format } from 'date-fns'
import { Pagination } from 'pobble-components/dist/pagination'
import MyLessonsContext from './my-lessons.context'
import BulkAction from './bulk-action/bulk-action'
import Skeleton from './skeleton/skeleton'
import Controls from './controls/controls'
import octopus from '../../../../../common/images/octopus.png'
import LessonsContext from '../../../lessons/lessons.context'
import { PresentationThumbnail } from 'pobble-components/dist/presentation'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLock } from '@fortawesome/pro-solid-svg-icons/faLock'
import { faGlobe } from '@fortawesome/pro-solid-svg-icons/faGlobe'
import { faCrown } from '@fortawesome/pro-solid-svg-icons/faCrown'
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes'
import { ShareModal } from '../../../../share/share'
import posthog from 'posthog-js'
import { apiClient } from 'pobble-components/dist/api'
import { useSubscriptionDetails } from 'pobble-components/dist/subscriptions/utils'
import { UpsellModal } from '../../../../pages/prompt/upsell'
import { openConfirmationDialog } from 'pobble-components/dist/modal/confirmation-dialog'
import { errorHandler } from 'pobble-components/dist/error-handler'
import ButtonElement from 'pobble-components/dist/button-element'
import { SaveForLaterModal } from '../../../prompt/save-for-later-modal'

const noResultsStyles = {
  container: {
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    flex: '1',
    marginTop: '60px',
    marginBottom: '20px',
    '& img': {
      width: '17.2em',
      height: '17.2em'
    },
    '& p': {
      margin: '0'
    }
  }
}

const NoResults = withRouter(
  injectSheet(noResultsStyles)(({ classes, children, history }) => {
    const { setCreateLessonOpened } = useContext(LessonsContext)
    return (
      <div className={classes.container}>
        <img src={octopus} alt="Octopus" />
        {children({ history, setCreateLessonOpened })}
      </div>
    )
  })
)

const lessonStyles = ({ COLORS, FONT_SIZES }) => ({
  publicCell: {
    fontWeight: 'bold'
  },
  workIcon: {
    color: COLORS.PURPLE.NORMAL,
    fontSize: '1.3em',
    verticalAlign: 'middle',
    marginRight: '.3em',
    marginTop: '-.1em'
  },
  workIconDelivered: {
    color: COLORS.GREEN.NORMAL
  },
  workIconNoAccess: {
    color: COLORS.RED.FADED
  },
  holder: {
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none'
  },
  thumbnail: {
    width: '160px',
    borderRadius: '4px',
    overflow: 'hidden',
    marginRight: '16px',
    border: '1px solid #ececec',
    display: 'none',
    position: 'relative',
    '@media (min-width: 541px)': {
      display: 'block'
    },
    '& > *': {
      fontSize: '10px'
    }
  },
  statusLabel: {
    display: 'none',
    '@media (min-width: 541px)': {
      display: 'inline'
    }
  },
  title: {
    ...FONT_SIZES.BODY_LARGE.STYLE,
    color: COLORS.BLUE.NORMAL,
    fontWeight: 'bold',
    display: 'block'
  },
  info: {
    color: COLORS.TEXT.LIGHT_GREY.NORMAL,
    ...FONT_SIZES.HEADING_NANO.STYLE,
    display: 'flex',
    '& svg': {
      color: '#c1c1c1',
      marginRight: '4px',
      fontSize: '.8em',
      marginTop: '-.2em'
    },
    '& > span:not(:first-child)': {
      position: 'relative',
      paddingLeft: '8px',
      marginLeft: '8px',
      '&:after': {
        content: '""',
        position: 'absolute',
        left: '0',
        top: '4px',
        width: '2px',
        height: '2px',
        background: 'currentColor',
        borderRadius: '100%'
      }
    }
  },
  crown: {
    color: '#000',
    position: 'absolute',
    right: '4px',
    top: '4px',
    zIndex: '100',
    fontSize: '12px',
    width: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fee966',
    borderRadius: '18px',
    border: '2px solid rgba(0,0,0,0.4)'
  },
  quickAction: {
    display: 'none',
    '@media (min-width: 768px)': {
      display: 'flex'
    }
  },
  accessDisabled: {
    '& img, & div>a, & td>div': {
      opacity: '0.3',
      pointerEvents: 'none'
    }
  },
  deliveryDateCell: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    whiteSpace: 'nowrap',
    '&:hover $removeDeliveryDate': {
      opacity: 1
    }
  },
  deliveryDate: {
    color: COLORS.BLUE.NORMAL,
    fontSize: '14px',
    fontWeight: 'bold'
  },
  removeDeliveryDate: {
    opacity: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    padding: 0,
    border: 'none',
    background: 'transparent',
    color: COLORS.TEXT.LIGHT_GREY.NORMAL,
    cursor: 'pointer',
    borderRadius: '4px',
    fontSize: '12px',
    transition: 'opacity 0.1s ease-in-out',
    '&:hover': {
      color: COLORS.RED.NORMAL,
      background: 'rgba(0, 0, 0, 0.05)'
    }
  },
  addDeliveryDate: {
    color: COLORS.BLUE.NORMAL,
    fontWeight: 'bold',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0,
    whiteSpace: 'nowrap',
    fontSize: '14px'
  }
})

const Lesson = injectSheet(lessonStyles)(
  ({ data, classes, pending, canSeePupils, first, ...props }) => {
    const dataMap = useMemo(
      () => {
        return {
          id: data.id,
          title: data.title,
          public: data.public,
          lessonLink: data.id,
          token: data.token,
          lastEdited: format(new Date(data.updated_at), 'DD-MM-YYYY'),
          premiumRestricted: data.policy.premium_restricted,
          deliveryDate: data.saved_lesson_delivery_date
            ? format(
                new Date(data.saved_lesson_delivery_date),
                'dddd D MMMM YYYY'
              )
            : null
        }
      },
      [data]
    )

    const { hasAccessToTL } = useSubscriptionDetails()
    const [upsellModalOpen, setUpsellModalOpen] = useState(false)
    const [deliveryDateModalOpen, setDeliveryDateModalOpen] = useState(false)
    const { refreshPage } = useContext(MyLessonsContext)

    const removeDeliveryDate = () => {
      openConfirmationDialog({
        theme: 'danger',
        bodyText:
          'Are you sure you want to remove the delivery date for this lesson?',
        action: () =>
          apiClient
            .delete(`/v3/presentations/${dataMap.id}/remove_delivery_date`)
            .then(refreshPage)
            .catch(errorHandler),
        confirmText: 'Remove',
        cancelText: 'Cancel'
      })
    }

    return (
      <>
        <TableRow
          className={
            !first && !hasAccessToTL ? classes.accessDisabled : undefined
          }
          pending={pending}>
          <Cell center>
            <BulkCheckbox id={dataMap.id} />
          </Cell>
          <Cell>
            <Link
              to={`/lesson/${dataMap.lessonLink}/`}
              onClick={e => {
                if (!first && !hasAccessToTL) {
                  setUpsellModalOpen('my-lessons')
                  e.preventDefault()
                }
              }}
              className={classes.holder}>
              <div className={classes.thumbnail}>
                {(dataMap.premiumRestricted || (!hasAccessToTL && !first)) && (
                  <span className={classes.crown}>
                    <FontAwesomeIcon icon={faCrown} />
                  </span>
                )}
                <PresentationThumbnail presentation={data} />
              </div>
              <div>
                <span className={classes.info}>
                  <span>{dataMap.lastEdited}</span>
                  {dataMap.public && (
                    <span>
                      <FontAwesomeIcon icon={faGlobe} />
                      <span className={classes.statusLabel}>Public</span>
                    </span>
                  )}
                  {!dataMap.public && (
                    <span>
                      <FontAwesomeIcon icon={faLock} />
                      <span className={classes.statusLabel}>Private</span>
                    </span>
                  )}
                </span>
                <span className={classes.title}>{dataMap.title}</span>
              </div>
            </Link>
          </Cell>
          <Cell>
            <div>
              {dataMap.deliveryDate ? (
                <span className={classes.deliveryDateCell}>
                  <div className={classes.deliveryDate}>
                    {dataMap.deliveryDate}
                  </div>
                  <ButtonElement
                    className={classes.removeDeliveryDate}
                    onClick={removeDeliveryDate}
                    aria-label="Remove delivery date">
                    <FontAwesomeIcon icon={faTimes} />
                  </ButtonElement>
                </span>
              ) : (
                <ButtonElement
                  className={classes.addDeliveryDate}
                  onClick={() => setDeliveryDateModalOpen(true)}>
                  Add a delivery date
                </ButtonElement>
              )}
            </div>
          </Cell>
          <Cell right>
            <div className={classes.actions}>
              <ContextMenu presentation={data} id={dataMap.id} {...props} />
            </div>
          </Cell>
        </TableRow>
        <UpsellModal
          id={upsellModalOpen}
          isOpen={!!upsellModalOpen}
          requestClose={() => setUpsellModalOpen(false)}
        />
        <SaveForLaterModal
          setDeliveryDateOnly
          isOpen={deliveryDateModalOpen}
          requestClose={() => setDeliveryDateModalOpen(false)}
          presentation={data}
          onSaved={refreshPage}
          location="my-lessons"
        />
      </>
    )
  }
)

const myLessonsStyles = {
  allCell: {
    width: '60px'
  },
  paginationHolder: {
    marginTop: '30px'
  },
  lastEdited: {
    width: '130px'
  },
  deliveryDate: {
    width: '200px',
    textAlign: 'left'
  },
  context: {
    width: '130px'
  },
  status: {
    width: '100px'
  },
  works: {
    width: '80px'
  },
  pupils: {
    width: '80px'
  },
  accessDisabled: {
    opacity: '0.3',
    pointerEvents: 'none'
  }
}

const MyLessons = ({
  classes,
  fetchPresentations,
  myPresentations,
  isUpdating,
  fetched,
  pagination,
  fetching,
  filters,
  setToPublic
}) => {
  const tableRef = useRef(null)

  const filtersApplied = useMemo(
    () =>
      !filters.search_string && (!filters.scope || filters.scope === 'all')
        ? false
        : true,
    [filters]
  )

  useEffect(() => {
    fetchPresentations({
      scope: filters.scope,
      page: pagination ? pagination.current : 1,
      limit: 10,
      initiator: 'FIRST_LOAD',
      search_string: filters.search_string
    })
  }, []) // eslint-disable-line

  useEffect(
    () => {
      posthog.capture('access my lessons')
      apiClient.post('/v3/me/activated_with_product', {
        type: 'content'
      })
      posthog.capture('activate', { type: 'content' })
      if (pagination) {
        posthog.capture('my lessons view', {
          pages: pagination.last,
          $set: { savedLessonsPages: pagination.last }
        })
      }
    },
    [pagination]
  )

  const onPageChange = state => {
    fetchPresentations({
      scope: filters.scope,
      page: state.selected,
      limit: 10,
      initiator: 'PAGINATION',
      search_string: filters.search_string
    })
  }

  const [shareModalState, setShareModalState] = useState({
    presentation: null,
    opened: false
  })

  const { me } = useMe()
  const canSeePupils = !!me.account.policy.can_see_actions

  const providerValue = useMemo(
    () => ({
      setShareModalState,
      refreshPage: () =>
        fetchPresentations({
          scope: filters.scope,
          page: pagination.current,
          limit: 10,
          search_string: filters.search_string
        })
    }),
    [pagination, setShareModalState] // eslint-disable-line
  )

  const { hasAccessToTL } = useSubscriptionDetails()

  return !fetching && !myPresentations.length && !filtersApplied ? (
    <NoResults>
      {({ history, setCreateLessonOpened }) => (
        <>
          <p>You don't have any lessons yet</p>
        </>
      )}
    </NoResults>
  ) : (
    <MyLessonsContext.Provider value={providerValue}>
      <div className={!hasAccessToTL ? classes.accessDisabled : undefined}>
        <Controls />
      </div>
      {!fetching && !myPresentations.length ? (
        <NoResults>
          {() => (
            <>
              <p>We couldn't find any lessons</p>
              <p>
                <strong>Try to change or clear filters</strong>
              </p>
            </>
          )}
        </NoResults>
      ) : (
        <BulkActionProvider entities={myPresentations}>
          <Table>
            <thead ref={tableRef}>
              <TableRow>
                <HeadCell className={classes.allCell}>
                  <SelectAllButton />
                </HeadCell>
                <HeadCell />
                <HeadCell className={classes.deliveryDate}>
                  Delivery date
                </HeadCell>
                <HeadCell className={classes.context} right>
                  <BulkAction />
                </HeadCell>
              </TableRow>
            </thead>
            {fetching && <Skeleton />}
            {!fetching &&
              !!myPresentations.length && (
                <tbody>
                  {myPresentations.map((presentation, index) => (
                    <Lesson
                      first={!index}
                      canSeePupils={canSeePupils}
                      pending={isUpdating(presentation.id)}
                      key={presentation.id}
                      data={presentation}
                      onShare={id =>
                        setShareModalState({
                          id,
                          title: presentation.title,
                          opened: true
                        })
                      }
                    />
                  ))}
                </tbody>
              )}
          </Table>
        </BulkActionProvider>
      )}
      <div className={!hasAccessToTL ? classes.accessDisabled : undefined}>
        <div className={classes.paginationHolder}>
          <Pagination
            scrollRef={tableRef}
            onPageChange={onPageChange}
            data={pagination}
          />
        </div>
      </div>
      <ShareModal
        from="my-lessons"
        userIsOwner
        presentation={shareModalState.presentation}
        isOpen={shareModalState.opened}
        requestClose={() =>
          setShareModalState({ ...shareModalState, opened: false })
        }
      />
    </MyLessonsContext.Provider>
  )
}

export default injectSheet(myLessonsStyles)(MyLessons)
